<template>
    <!--
        Deal detail drawer — slides in from the right when a table row is
        clicked. Populated from the row data already on the client (no extra
        fetch): header, money stats, detail rows, and footer links to the
        full edit record. The designer's Activity timeline is intentionally
        omitted for now — the backend doesn't expose a per-deal activity
        feed yet; wire it in when that endpoint exists.
    -->
    <aside :style="rootStyle">
        <!-- Header -->
        <div :style="headerStyle">
            <div :style="{ flex: '1 1 auto', minWidth: 0 }">
                <div :style="headerTopStyle">
                    <span class="tag-mono" :style="dealIdStyle">DEAL #{{ dealId }}</span>
                    <DealStatusPill v-if="statusLabel" :status="statusLabel" />
                </div>
                <h2 :style="nameStyle">{{ dealName }}</h2>
                <div :style="subStyle">
                    <Avatar v-if="agencyName" :name="agencyName" :size="16" />
                    <span v-if="agencyName">{{ agencyName }}</span>
                    <span v-if="agencyName && partnerName">·</span>
                    <span v-if="partnerName">{{ partnerName }}</span>
                </div>
            </div>
            <button type="button" class="btn btn--icon btn--xs btn--ghost" aria-label="Close" @click="$emit('close')">
                <Icon name="x" />
            </button>
        </div>

        <!-- Money stats -->
        <div :style="sectionStyle">
            <div :style="statsGridStyle">
                <div :style="statBoxStyle">
                    <div :style="statLabelStyle">Invoiced</div>
                    <div class="tag-mono tag-tnum" :style="statValueStyle">{{ fmt(invoiced) }}</div>
                </div>
                <div :style="statBoxStyle">
                    <div :style="statLabelStyle">Collected</div>
                    <div class="tag-mono tag-tnum" :style="statValueStyle">{{ fmt(collected) }}</div>
                    <div v-if="outstanding > 0" class="tag-mono tag-tnum" :style="outstandingStyle">
                        −{{ fmt(outstanding) }} outstanding
                    </div>
                </div>
                <div :style="statBoxStyle">
                    <div :style="statLabelStyle">Commission</div>
                    <div class="tag-mono tag-tnum" :style="statValueAccentStyle">{{ fmt(commission) }}</div>
                </div>
            </div>
        </div>

        <!-- Details -->
        <div :style="sectionStyle">
            <div :style="sectionTitleStyle">Details</div>
            <div v-if="dealDate" :style="detailRowStyle">
                <span :style="detailKeyStyle">Date opened</span>
                <span class="tag-mono tag-tnum" :style="detailValStyle">{{ dealDate }}</span>
            </div>
            <div v-if="agencyName" :style="detailRowStyle">
                <span :style="detailKeyStyle">Agency</span>
                <span :style="detailValStyle">
                    <a v-if="agencyHref" :href="agencyHref" target="_blank" :style="detailLinkStyle">{{ agencyName }} ↗</a>
                    <template v-else>{{ agencyName }}</template>
                </span>
            </div>
            <div v-if="brandName" :style="detailRowStyle">
                <span :style="detailKeyStyle">Brand</span>
                <span :style="detailValStyle">{{ brandName }}</span>
            </div>
            <div v-if="partnerName" :style="detailRowStyle">
                <span :style="detailKeyStyle">Partner</span>
                <span :style="detailValStyle">{{ partnerName }}</span>
            </div>
            <div v-if="statusLabel" :style="detailRowStyle">
                <span :style="detailKeyStyle">Status</span>
                <span :style="detailValStyle"><DealStatusPill :status="statusLabel" /></span>
            </div>
        </div>

        <div :style="{ flex: '1 1 auto' }" />

        <!-- Footer -->
        <div :style="footerStyle">
            <a v-if="recordHref" :href="recordHref" class="btn btn--sm" :style="{ flex: '1 1 auto', gap: '5px', textDecoration: 'none' }">
                <Icon name="edit" /> Edit
            </a>
            <a v-if="recordHref" :href="recordHref" class="btn btn--primary btn--sm" :style="{ flex: '1 1 auto', textDecoration: 'none' }">
                Open full record
            </a>
        </div>
    </aside>
</template>

<script>
import Icon from "../../Agencies/_atoms/Icon.vue";
import Avatar from "../../Agencies/_atoms/Avatar.vue";
import DealStatusPill from "./DealStatusPill.vue";

export default {
    name: "DealDrawer",
    components: { Icon, Avatar, DealStatusPill },
    props: {
        deal: { type: Object, default: null },
    },
    computed: {
        dealId() {
            return this.deal ? this.deal.id : "";
        },
        dealName() {
            return this.nameOf(this.deal && this.deal.name);
        },
        statusLabel() {
            return this.deal ? String(this.deal.status || "") : "";
        },
        agencyName() {
            return this.nameOf(this.deal && this.deal.agency);
        },
        agencyHref() {
            return this.hrefOf(this.deal && this.deal.agency);
        },
        partnerName() {
            return this.nameOf(this.deal && this.deal.partner);
        },
        brandName() {
            return this.nameOf(this.deal && this.deal.brand);
        },
        dealDate() {
            return this.deal ? this.deal.date || "" : "";
        },
        invoiced() {
            return this.num(this.deal && this.deal.amount_invoiced);
        },
        collected() {
            return this.num(this.deal && this.deal.amount_collect);
        },
        commission() {
            return this.num(this.deal && this.deal.commission);
        },
        outstanding() {
            const diff = this.invoiced - this.collected;
            return diff > 0 ? diff : 0;
        },
        recordHref() {
            // Prefer the deal-name link from the row; fall back to the
            // conventional edit route.
            const nameHref = this.hrefOf(this.deal && this.deal.name);
            if (nameHref) return nameHref;
            return this.dealId ? `/deals/${this.dealId}/edit` : null;
        },
        // ===== styles =====
        rootStyle() {
            return {
                width: "420px",
                flex: "0 0 420px",
                background: "var(--surface)",
                borderLeft: "1px solid var(--border)",
                display: "flex",
                flexDirection: "column",
                boxShadow: "var(--shadow-lg)",
                overflow: "auto",
            };
        },
        headerStyle() {
            return {
                padding: "14px 18px 12px",
                borderBottom: "1px solid var(--border-faint)",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
            };
        },
        headerTopStyle() {
            return { display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" };
        },
        dealIdStyle() {
            return { fontSize: "11px", color: "var(--text-faint)" };
        },
        nameStyle() {
            return { font: "600 17px/1.25 var(--font-display)", letterSpacing: "-0.015em", margin: 0 };
        },
        subStyle() {
            return { fontSize: "12.5px", color: "var(--text-muted)", marginTop: "4px", display: "flex", alignItems: "center", gap: "6px" };
        },
        sectionStyle() {
            return { padding: "14px 18px", borderBottom: "1px solid var(--border-faint)" };
        },
        statsGridStyle() {
            return { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "8px" };
        },
        statBoxStyle() {
            return {
                padding: "10px",
                background: "var(--surface-2)",
                border: "1px solid var(--border-faint)",
                borderRadius: "var(--r-md)",
            };
        },
        statLabelStyle() {
            return {
                fontSize: "10.5px",
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: "4px",
            };
        },
        statValueStyle() {
            return { fontSize: "17px", fontWeight: 500, color: "var(--text)" };
        },
        statValueAccentStyle() {
            return { fontSize: "17px", fontWeight: 500, color: "var(--tag-red)" };
        },
        outstandingStyle() {
            return { fontSize: "10.5px", color: "var(--st-overdue-fg)", marginTop: "2px" };
        },
        sectionTitleStyle() {
            return {
                fontSize: "11.5px",
                fontWeight: 500,
                color: "var(--text-muted)",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                marginBottom: "10px",
            };
        },
        detailRowStyle() {
            return { display: "flex", padding: "5px 0", fontSize: "12.5px", gap: "12px" };
        },
        detailKeyStyle() {
            return { color: "var(--text-muted)", width: "110px", flex: "0 0 110px" };
        },
        detailValStyle() {
            return { color: "var(--text)", fontWeight: 450, flex: "1 1 auto", minWidth: 0 };
        },
        detailLinkStyle() {
            return { color: "var(--tag-red)", fontWeight: 500, textDecoration: "none" };
        },
        footerStyle() {
            return { padding: "12px 18px", borderTop: "1px solid var(--border)", display: "flex", gap: "8px" };
        },
    },
    methods: {
        nameOf(v) {
            if (v && typeof v === "object") return v.name || "";
            return v || "";
        },
        hrefOf(v) {
            if (v && typeof v === "object") return v.link || null;
            return null;
        },
        num(v) {
            if (v == null || v === "") return 0;
            const n = Number(String(v).replace(/[,$\s]/g, ""));
            return Number.isFinite(n) ? n : 0;
        },
        fmt(n) {
            return "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
    },
};
</script>
